/**
 * Centralized enum definitions
 * Single source of truth for all enum values used across database schema and application logic
 */

// Procedure Categories
export const PROCEDURE_CATEGORIES = [
  'Physical Exams',
  'Hearing',
  'Vision',
  'Other',
  'Physical Abilities Testing',
  'Immunizations',
  'Specimen Collections',
  'Diagnostic Testing',
] as const;

// Contact Types
export const CONTACT_TYPES = [
  'Contracts',
  'IT Security/Compliance',
  'Accounts Receivable',
  'Scheduling',
  'Exam Results',
  'Credentialing',
  'Office Manager',
] as const;

// Clinic Types
export const CLINIC_TYPES = ['Clinic', 'MEU', 'Mobile'] as const;

// Clinic Specialties
export const CLINIC_SPECIALTIES = [
  'Audiology',
  'Optometry',
  'Lab',
  'Psychiatry',
  'OccMed',
  'Immunizations',
  'Collection',
  'PT',
  'Radiology',
  'Cardiology',
] as const;

// Component Statuses (DB-backed: persisted in the clinic.components JSONB
// blob as the `status` field on each component entry).
export const COMPONENT_DB_STATUSES = [
  'enabled',
  'disabled',
  'unknown',
] as const;

// Component Filter Statuses: extends DB values with 'suspended', a
// filter-only sentinel that maps to a `suspend IS NOT NULL` predicate
// in buildComponentFilters. Never written to the DB.
export const COMPONENT_FILTER_STATUSES = [
  ...COMPONENT_DB_STATUSES,
  'suspended',
] as const;

// Component View Statuses: extends DB values with 'notOnContract', a
// computed view-layer state set by buildComponentInfo when a component
// isn't present on the clinic's active contract. Returned to the UI.
export const COMPONENT_VIEW_STATUSES = [
  ...COMPONENT_DB_STATUSES,
  'notOnContract',
] as const;

// Feature Statuses (used for both the per-clinic JSONB feature flags and
// the search-procedure filter — both currently align on the same 3 values).
export const FEATURE_STATUSES = ['enabled', 'disabled', 'unknown'] as const;

// Network Statuses (DB-backed values for the network_status column)
export const NETWORK_STATUSES = ['in', 'out'] as const;

// Network Status Filters: extends DB values with 'unspecified', a filter-only
// sentinel that means "rows where networkStatus IS NULL". Never persisted.
export const NETWORK_STATUS_FILTERS = [
  ...NETWORK_STATUSES,
  'unspecified',
] as const;

// Provider Group Statuses
export const PROVIDER_GROUP_STATUSES = [
  'lead',
  'active',
  'onHold',
  'inactive',
  'billing',
  'recruitment',
] as const;

// Address Types
export const ADDRESS_TYPES = ['Physical', 'Billing', 'Mailing'] as const;

// Feature Rule States (for provider group level)
export const FEATURE_RULE_STATES = ['none', 'enabled', 'disabled'] as const;

// Feature Override States (for clinic level)
export const FEATURE_OVERRIDE_STATES = [
  'useGroup',
  'enabled',
  'disabled',
] as const;

// Provider Types (for credentialing)
export const PROVIDER_TYPES = [
  'MD',
  'DO',
  'NP',
  'PA',
  'RN',
  'LPN',
  'PT',
  'OT',
  'Other',
] as const;

// Provider Statuses (for credentialing)
export const PROVIDER_STATUSES = ['active', 'inactive', 'suspended'] as const;

// Certification Types (for credentialing)
export const CERTIFICATION_TYPES = [
  'Board Certified',
  'DOT',
  'FAA',
  'NRCME',
  'Other',
] as const;

// Insurance Types (for credentialing)
export const INSURANCE_TYPES = [
  'Professional Liability',
  'Malpractice',
  'General Liability',
  'Other',
] as const;

// Adverse Action Types (for credentialing)
export const ADVERSE_ACTION_TYPES = [
  'License Action',
  'Malpractice Action',
  'Felony Conviction',
  'Other',
] as const;

// Credential Status (for licenses, certifications, insurance)
export const CREDENTIAL_STATUS = [
  'active',
  'expired',
  'suspended',
  'pending',
  'revoked',
] as const;

// Audit Actions (for credential audit log)
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'verify',
  'expire',
  'alert',
  'view',
] as const;

// Group / Clinic business document types (stored as JSONB on groups/clinics)
export const GROUP_DOCUMENT_TYPES = [
  'Terms',
  'Fees',
  'Roster',
  'Profile',
  'W9',
  'RNS',
  'COI',
  'Other',
] as const;

// Credentialing document types (stored on provider_documents table)
export const CREDENTIALING_DOCUMENT_TYPES = [
  'professional_license',
  'board_certification',
  'malpractice_insurance',
  'sanctions_screening',
  'immunization_records',
  'federal_portability',
  'privilege_scope',
  'other',
] as const;

// Note Types
export const NOTE_TYPES = ['Phone', 'Email', 'Fax', 'Other'] as const;

// Contract Statuses
export const CONTRACT_STATUSES = ['draft', 'active'] as const;

// Ticket Statuses
export const TICKET_STATUSES = ['todo', 'in_progress', 'done'] as const;

// Ticket Priorities
export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

// Intake Job Statuses
export const INTAKE_JOB_STATUSES = [
  'processing',
  'completed',
  'failed',
] as const;

// Analytics Report Statuses
export const REPORT_STATUSES = [
  'queued',
  'processing',
  'completed',
  'failed',
] as const;

// Distance Types (analytics geo reports)
export const DISTANCE_TYPES = ['radius', 'driving'] as const;

// Provider Lifecycle States (credentialing)
export const LIFECYCLE_STATES = [
  'draft',
  'pending',
  'credentialed',
  'privileged',
  'active',
  'suspended',
  'expired',
  'terminated',
] as const;

// User Roles (Azure AD / role-based authorization)
export const USER_ROLES = ['Admin', 'PNOps', 'ViewOnly'] as const;

// Provider Network Sort Options
export const PROVIDER_NETWORK_SORT_BY = [
  'clinicCode',
  'clinicName',
  'groupName',
  'city',
  'state',
  'county',
  'type',
  'createdAt',
  'status',
  'phone',
  'zipCode',
] as const;

// TypeScript utility types for enum values
export type ProcedureCategory = (typeof PROCEDURE_CATEGORIES)[number];
export type ContactType = (typeof CONTACT_TYPES)[number];
export type ClinicType = (typeof CLINIC_TYPES)[number];
export type NetworkStatus = (typeof NETWORK_STATUSES)[number];
export type NetworkStatusFilter = (typeof NETWORK_STATUS_FILTERS)[number];
export type ComponentDbStatus = (typeof COMPONENT_DB_STATUSES)[number];
export type ComponentFilterStatus = (typeof COMPONENT_FILTER_STATUSES)[number];
export type ComponentViewStatus = (typeof COMPONENT_VIEW_STATUSES)[number];
export type FeatureStatus = (typeof FEATURE_STATUSES)[number];
export type ClinicSpecialty = (typeof CLINIC_SPECIALTIES)[number];
export type ProviderGroupStatus = (typeof PROVIDER_GROUP_STATUSES)[number];
export type AddressType = (typeof ADDRESS_TYPES)[number];
export type FeatureRuleState = (typeof FEATURE_RULE_STATES)[number];
export type FeatureOverrideState = (typeof FEATURE_OVERRIDE_STATES)[number];
export type ProviderType = (typeof PROVIDER_TYPES)[number];
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];
export type CertificationType = (typeof CERTIFICATION_TYPES)[number];
export type InsuranceType = (typeof INSURANCE_TYPES)[number];
export type AdverseActionType = (typeof ADVERSE_ACTION_TYPES)[number];
export type CredentialStatus = (typeof CREDENTIAL_STATUS)[number];
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export type GroupDocumentType = (typeof GROUP_DOCUMENT_TYPES)[number];
export type CredentialingDocumentType =
  (typeof CREDENTIALING_DOCUMENT_TYPES)[number];
export type NoteType = (typeof NOTE_TYPES)[number];
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export type TicketStatus = (typeof TICKET_STATUSES)[number];
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];
export type IntakeJobStatus = (typeof INTAKE_JOB_STATUSES)[number];
export type ReportStatus = (typeof REPORT_STATUSES)[number];
export type DistanceType = (typeof DISTANCE_TYPES)[number];
export type LifecycleState = (typeof LIFECYCLE_STATES)[number];
export type UserRole = (typeof USER_ROLES)[number];
export type ProviderNetworkSortBy = (typeof PROVIDER_NETWORK_SORT_BY)[number];
